import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Mic, MicOff, Send, Volume2, VolumeX, Sparkles, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import LanguageSelector from "./LanguageSelector";
import VoiceWaveform from "./VoiceWaveform";
import MessageContent from "./MessageContent";
import { sendChat, transcribeAudio, ttsSpeak, fetchRegion, submitLead, declineLead, fetchTopFaq, reportClientError } from "@/lib/api";
import { STRINGS, isRtl } from "@/lib/i18n";
import { makeSpeakable } from "@/lib/speakable";

export default function ChatWidget({ lang, setLang, initialOpen = false, embed = false }) {
  const t = STRINGS[lang];
  const rtl = isRtl(lang);
  const [open, setOpen] = useState(initialOpen);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sessionId, setSessionId] = useState(() => {
    try { return localStorage.getItem("drht_session_id") || null; } catch { return null; }
  });
  const [country, setCountry] = useState("OTHER");
  const [busy, setBusy] = useState(false);
  const [recording, setRecording] = useState(false);
  const [voiceOn, setVoiceOn] = useState(() => {
    try { return localStorage.getItem("drht_voice_on") !== "0"; } catch { return true; }
  });
  const [showLead, setShowLead] = useState(false);
  const [isCampaignLead, setIsCampaignLead] = useState(false);
  const [askContactInline, setAskContactInline] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === "undefined") return false;
    // Combine three signals so we catch real phones even when matchMedia is misleading
    // (some Android browsers / iframes report stale viewport widths until first layout).
    const byWidth = window.innerWidth <= 640;
    const byMq = window.matchMedia("(max-width: 640px)").matches;
    const byUa = /Android|iPhone|iPad|iPod|Mobile|webOS|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent || "");
    return byWidth || byMq || byUa;
  });
  const [showFaq, setShowFaq] = useState(false);
  const [faqList, setFaqList] = useState([]);
  const [faqLoading, setFaqLoading] = useState(false);
  const [leadData, setLeadData] = useState({ name: "", contact_method: "phone", contact_value: "" });
  const [leadDone, setLeadDone] = useState(() => {
    try { return localStorage.getItem("drht_lead_done") === "1"; } catch { return false; }
  });
  const [showDeclineForm, setShowDeclineForm] = useState(false);
  const [declineText, setDeclineText] = useState("");
  const [micPermission, setMicPermission] = useState(() => {
    try { return localStorage.getItem("drht_mic_perm") || "prompt"; } catch { return "prompt"; }
  }); // 'prompt' | 'granted' | 'denied'
  const [micRequesting, setMicRequesting] = useState(false);
  const scrollRef = useRef(null);
  const audioRef = useRef(null);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);

  // Persist session id whenever it changes
  useEffect(() => {
    try {
      if (sessionId) localStorage.setItem("drht_session_id", sessionId);
    } catch {/* ignore quota / private mode */}
  }, [sessionId]);

  // Persist voice on/off preference
  useEffect(() => {
    try { localStorage.setItem("drht_voice_on", voiceOn ? "1" : "0"); } catch {/* ignore */}
  }, [voiceOn]);

  // Responsive: detect mobile vs desktop for layout switch (iPhone frame vs WhatsApp full)
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 640px)");
    const recompute = () => {
      const byWidth = window.innerWidth <= 640;
      const byMq = mq.matches;
      const byUa = /Android|iPhone|iPad|iPod|Mobile|webOS|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent || "");
      setIsMobile(byWidth || byMq || byUa);
    };
    mq.addEventListener?.("change", recompute);
    window.addEventListener("resize", recompute);
    // Re-check after first paint (some Android browsers report stale innerWidth on initial load)
    const t1 = setTimeout(recompute, 250);
    return () => {
      mq.removeEventListener?.("change", recompute);
      window.removeEventListener("resize", recompute);
      clearTimeout(t1);
    };
  }, []);

  // Check microphone permission on open (Permissions API where supported)
  useEffect(() => {
    if (!open) return;
    if (!navigator.permissions || !navigator.permissions.query) return;
    navigator.permissions.query({ name: "microphone" }).then((status) => {
      setMicPermission(status.state); // 'granted' | 'denied' | 'prompt'
      try { localStorage.setItem("drht_mic_perm", status.state); } catch {/* ignore */}
      status.onchange = () => {
        setMicPermission(status.state);
        try { localStorage.setItem("drht_mic_perm", status.state); } catch {/* ignore */}
      };
    }).catch(() => {/* unsupported on iOS Safari */});
  }, [open]);

  const loadFaq = async () => {
    if (faqList.length > 0) { setShowFaq((v) => !v); return; }
    setFaqLoading(true);
    try {
      const r = await fetchTopFaq(lang);
      setFaqList(r.questions || []);
      setShowFaq(true);
    } catch {/* ignore */} finally { setFaqLoading(false); }
  };


  const requestMicPermission = async () => {
    if (micRequesting) return;
    setMicRequesting(true);
    try {
      // 1) Request microphone access
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach((t) => t.stop());

      // 2) iOS / Safari audio unlock — must happen INSIDE the same user gesture.
      // a) Unlock HTMLAudioElement playback (for our /api/tts blob audio)
      try {
        if (audioRef.current) {
          audioRef.current.muted = true;
          // tiny silent wav data URI (~0.1s)
          const silent = "data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQAAAAA=";
          audioRef.current.src = silent;
          await audioRef.current.play().catch(() => {});
          audioRef.current.pause();
          audioRef.current.muted = false;
          audioRef.current.src = "";
        }
      } catch (_) {/* ignore */}
      // b) Unlock Web Speech Synthesis (browser TTS fallback) on iOS Safari
      try {
        if (window.speechSynthesis) {
          const ping = new SpeechSynthesisUtterance(" ");
          ping.volume = 0; ping.rate = 1;
          window.speechSynthesis.cancel();
          window.speechSynthesis.speak(ping);
        }
      } catch (_) {/* ignore */}
      // c) Unlock AudioContext too (some iOS browsers need this)
      try {
        const AC = window.AudioContext || window.webkitAudioContext;
        if (AC) {
          const ctx = new AC();
          await ctx.resume?.();
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          gain.gain.value = 0;
          osc.connect(gain); gain.connect(ctx.destination);
          osc.start(0); osc.stop(ctx.currentTime + 0.01);
          setTimeout(() => ctx.close?.(), 100);
        }
      } catch (_) {/* ignore */}

      setMicPermission("granted");
      try { localStorage.setItem("drht_mic_perm", "granted"); } catch {/* ignore */}
      toast.success(
        lang === "tr" ? "Mikrofon ve ses açıldı 🎤🔊"
        : lang === "en" ? "Microphone & audio enabled 🎤🔊"
        : lang === "ar" ? "تم تفعيل الميكروفون والصوت 🎤🔊"
        : "Микрофон и звук включены 🎤🔊"
      );
    } catch (e) {
      setMicPermission("denied");
      try { localStorage.setItem("drht_mic_perm", "denied"); } catch {/* ignore */}
      toast.error(
        lang === "tr" ? "Mikrofon izni reddedildi. Tarayıcı ayarlarından açabilirsiniz."
        : lang === "en" ? "Microphone permission denied. Enable it in browser settings."
        : lang === "ar" ? "تم رفض إذن الميكروفون. فعّله من إعدادات المتصفح."
        : "Доступ к микрофону отклонён. Включите его в настройках браузера."
      );
    } finally {
      setMicRequesting(false);
    }
  };

  // Restore previous conversation history when an existing session is loaded
  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/conversations/${sessionId}`);
        if (!r.ok) return;
        const data = await r.json();
        if (cancelled || !Array.isArray(data) || data.length === 0) return;
        const restored = data.slice(-30).map((m) => ({ role: m.role, content: m.content }));
        setMessages(restored);
      } catch {/* keep welcome message */}
    })();
    return () => { cancelled = true; };
  }, []); // run once on mount

  useEffect(() => {
    fetchRegion().then((r) => setCountry(r.country)).catch(() => {});
  }, []);

  // Show invite popup almost immediately on every page visit (not dismissed)
  useEffect(() => {
    if (sessionStorage.getItem("invite_dismissed")) return;
    const timer = setTimeout(() => {
      if (!open) setShowInvite(true);
    }, 1200);
    return () => clearTimeout(timer);
  }, [open]);

  const dismissInvite = () => {
    setShowInvite(false);
    // Only suppress for the current tab session — next visit shows it again
    sessionStorage.setItem("invite_dismissed", "1");
  };

  const acceptInvite = () => {
    dismissInvite();
    setOpen(true);
  };

  useEffect(() => {
    if (open && messages.length === 0) {
      setMessages([{ role: "assistant", content: t.welcome }]);
    }
  }, [open, lang]); // eslint-disable-line

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
    }
  }, [messages, busy]);

  const playTTS = async (text) => {
    if (!voiceOn) return;
    const speakable = makeSpeakable(text, lang);
    if (!speakable) return;
    try {
      const blob = await ttsSpeak(speakable);
      const url = URL.createObjectURL(blob);
      if (audioRef.current) {
        audioRef.current.src = url;
        try {
          await audioRef.current.play();
        } catch (playErr) {
          // iOS Safari: autoplay blocked — flag last message so user can tap to listen
          reportClientError({ kind: "audio_play_failed", message: String(playErr).slice(0, 200), language: lang, session_id: sessionId });
          setMessages((m) => {
            const copy = [...m];
            for (let i = copy.length - 1; i >= 0; i--) {
              if (copy[i].role === "assistant") { copy[i] = { ...copy[i], needsTap: true, blobUrl: url }; break; }
            }
            return copy;
          });
        }
      }
    } catch (e) {
      reportClientError({ kind: "tts_failed", message: String(e?.message || e).slice(0, 200), language: lang, session_id: sessionId });
      // Fallback to browser SpeechSynthesis with a MALE voice when possible
      try {
        const utt = new SpeechSynthesisUtterance(speakable);
        const langCode = lang === "ar" ? "ar-SA" : lang === "ru" ? "ru-RU" : lang === "en" ? "en-US" : "tr-TR";
        utt.lang = langCode;
        utt.pitch = 0.9;
        utt.rate = 1.0;
        const voices = window.speechSynthesis.getVoices();
        const maleHints = ["male", "tolga", "yelda", "yunus", "erkek", "david", "mark", "george", "daniel", "fred", "alex", "diego", "yuri", "pavel"];
        const langVoices = voices.filter((v) => v.lang && v.lang.toLowerCase().startsWith(langCode.toLowerCase().split("-")[0]));
        const pickMale = (arr) => arr.find((v) => maleHints.some((h) => v.name.toLowerCase().includes(h)));
        const chosen = pickMale(langVoices) || pickMale(voices) || langVoices[0] || voices[0];
        if (chosen) utt.voice = chosen;
        window.speechSynthesis.cancel();
        window.speechSynthesis.speak(utt);
      } catch (_) {}
    }
  };

  const replayMessage = async (msgIdx) => {
    const m = messages[msgIdx];
    if (!m?.blobUrl || !audioRef.current) return;
    audioRef.current.src = m.blobUrl;
    try {
      await audioRef.current.play();
      setMessages((arr) => arr.map((x, i) => i === msgIdx ? { ...x, needsTap: false } : x));
    } catch (e) {
      toast.error(lang === "tr" ? "Ses çalınamadı" : "Audio play failed");
    }
  };

  const send = async (textOverride) => {
    const text = (textOverride ?? input).trim();
    if (!text || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }]);
    setBusy(true);
    try {
      const res = await sendChat({ session_id: sessionId, message: text, language: lang, country });
      if (!sessionId) setSessionId(res.session_id);
      setMessages((m) => [...m, {
        role: "assistant",
        content: res.reply,
        whatsappTattoo: !!res.whatsapp_tattoo,
        whatsappEndolift: !!res.whatsapp_endolift,
      }]);
      if (res.show_lead_form) setShowLead(true);
      if (res.is_campaign_request) setIsCampaignLead(true);
      if (res.ask_contact && !res.show_lead_form && !res.whatsapp_tattoo && !res.whatsapp_endolift) setAskContactInline(true);
      playTTS(res.reply);
    } catch (e) {
      toast.error("Bir hata oluştu / Error");
    } finally {
      setBusy(false);
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream, { mimeType: "audio/webm" });
      chunksRef.current = [];
      mr.ondataavailable = (e) => e.data.size > 0 && chunksRef.current.push(e.data);
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        setBusy(true);
        try {
          const r = await transcribeAudio(blob, lang);
          if (r.text && r.text.trim()) {
            await send(r.text);
          } else {
            toast.error(lang === "tr" ? "Ses anlaşılmadı" : "Could not understand audio");
          }
        } catch (e) {
          toast.error(lang === "tr" ? "Ses tanıma çalışmıyor" : "STT unavailable");
        } finally {
          setBusy(false);
        }
      };
      mediaRef.current = mr;
      mr.start();
      setRecording(true);
    } catch (e) {
      setMicPermission("denied");
      try { localStorage.setItem("drht_mic_perm", "denied"); } catch {/* ignore */}
      toast.error(
        lang === "tr" ? "Mikrofona erişilemedi. Lütfen tarayıcı izinlerini kontrol edin."
        : lang === "en" ? "Couldn't access microphone. Please check browser permissions."
        : lang === "ar" ? "تعذر الوصول إلى الميكروفون. يرجى التحقق من أذونات المتصفح."
        : "Не удалось получить доступ к микрофону. Проверьте разрешения браузера."
      );
    }
  };

  const stopRecording = () => {
    if (mediaRef.current && mediaRef.current.state !== "inactive") {
      mediaRef.current.stop();
    }
    setRecording(false);
  };

  // WhatsApp-style press-and-hold mic UX
  const pressTimerRef = useRef(null);
  const isHoldingRef = useRef(false);
  const recordStartRef = useRef(0);

  const handleMicPointerDown = (e) => {
    if (busy || recording) return;
    e.preventDefault?.();
    isHoldingRef.current = true;
    // Start after 200ms hold to differentiate from a quick tap
    pressTimerRef.current = setTimeout(() => {
      if (!isHoldingRef.current) return;
      recordStartRef.current = Date.now();
      startRecording();
    }, 180);
  };

  const handleMicPointerUp = () => {
    isHoldingRef.current = false;
    if (pressTimerRef.current) {
      clearTimeout(pressTimerRef.current);
      pressTimerRef.current = null;
    }
    if (recording) {
      const dur = Date.now() - recordStartRef.current;
      if (dur < 400) {
        // Too short — cancel without sending
        if (mediaRef.current && mediaRef.current.state !== "inactive") {
          // Replace onstop with a no-op cancel to avoid sending tiny snippet
          mediaRef.current.onstop = () => {
            mediaRef.current.stream?.getTracks?.().forEach((t) => t.stop());
          };
          mediaRef.current.stop();
        }
        setRecording(false);
        toast.info(
          lang === "tr" ? "Konuşmak için mikrofonu basılı tutun"
          : lang === "en" ? "Hold the mic button to record"
          : lang === "ar" ? "اضغط مع الاستمرار على الميكروفون"
          : "Удерживайте кнопку микрофона"
        );
        return;
      }
      stopRecording(); // will auto-send via onstop handler
    }
  };

  const handleMicPointerCancel = () => {
    isHoldingRef.current = false;
    if (pressTimerRef.current) { clearTimeout(pressTimerRef.current); pressTimerRef.current = null; }
    if (recording) stopRecording();
  };

  const handleLead = async (e) => {
    e.preventDefault();
    if (!leadData.name || !leadData.contact_value) return;
    try {
      await submitLead({
        session_id: sessionId,
        name: leadData.name,
        contact_method: leadData.contact_method,
        contact_value: leadData.contact_value,
        language: lang,
        is_campaign: isCampaignLead,
      });
      setLeadDone(true);
      try { localStorage.setItem("drht_lead_done", "1"); } catch {/* ignore */}
      const thanksMsg = isCampaignLead
        ? (lang === "tr"
            ? "Teşekkürler! 🎁 Aktif kampanyalarımızı şimdi sizinle paylaşacağım..."
            : lang === "en"
            ? "Thank you! 🎁 Now I'll share our active campaigns with you..."
            : lang === "ar"
            ? "شكرًا! 🎁 سأشارك معك العروض النشطة الآن..."
            : "Спасибо! 🎁 Сейчас поделюсь активными акциями...")
        : t.lead_thanks;
      setMessages((m) => [...m, { role: "assistant", content: thanksMsg }]);
      playTTS(thanksMsg);
      setTimeout(() => setShowLead(false), 1800);
      // If campaign request, automatically follow-up so AI now reveals details
      if (isCampaignLead) {
        const followUp = lang === "tr"
          ? "Aktif kampanyalarınızı paylaşır mısınız?"
          : lang === "en"
          ? "Could you share your active campaigns now?"
          : lang === "ar"
          ? "هل يمكنك مشاركة العروض النشطة الآن؟"
          : "Поделитесь, пожалуйста, активными акциями.";
        setIsCampaignLead(false);
        setTimeout(() => send(followUp), 2000);
      }
    } catch {
      toast.error("Submit failed");
    }
  };

  return (
    <>
      <audio ref={audioRef} hidden />
      {/* Floating orb */}
      <AnimatePresence>
        {!open && (
          <motion.button
            data-testid="chat-orb"
            initial={{ opacity: 0, scale: 0.6 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.6 }}
            onClick={() => { dismissInvite(); setOpen(true); }}
            className="fixed bottom-20 right-6 z-50 group"
          >
            <span className="absolute inset-0 rounded-full orb-pulse"
              style={{ background: "radial-gradient(circle, #B39B7A 0%, #8B9B88 100%)" }} />
            <span className="relative block w-16 h-16 rounded-full overflow-hidden shadow-2xl ring-2 ring-white/60"
              style={{ background: "linear-gradient(135deg, #B39B7A 0%, #8B9B88 100%)" }}>
              <img
                src="/dr-tirman-avatar.png"
                alt="Dr. Hüseyin Tırman"
                className="absolute inset-0 w-full h-full object-contain object-center"
                draggable={false}
              />
              {/* Live "speaking" dot */}
              <span className="absolute bottom-0.5 right-0.5 w-3.5 h-3.5 rounded-full bg-emerald-500 ring-2 ring-white" />
            </span>
            <span className="absolute -top-12 right-0 whitespace-nowrap glass px-3 py-1.5 rounded-full text-xs font-display opacity-0 group-hover:opacity-100 transition-opacity"
              style={{ color: "#2C3531" }}>
              {t.hero_cta}
            </span>
          </motion.button>
        )}
      </AnimatePresence>

      {/* Welcome invite popup */}
      <AnimatePresence>
        {showInvite && !open && (
          <motion.div
            data-testid="chat-invite"
            initial={{ opacity: 0, y: 20, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.9 }}
            transition={{ type: "spring", damping: 22 }}
            className="fixed bottom-44 right-6 z-50 max-w-[300px]"
          >
            <div className="glass rounded-2xl p-4 shadow-2xl relative" style={{ borderColor: "rgba(179,155,122,0.3)" }}>
              <button
                onClick={dismissInvite}
                data-testid="invite-dismiss"
                className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-white border border-border/40 flex items-center justify-center text-muted-foreground hover:text-foreground"
              >
                <X className="w-3 h-3" />
              </button>
              <div className="flex items-start gap-3">
                <div className="relative w-12 h-12 rounded-full flex-shrink-0 overflow-hidden ring-2 ring-white/70 shadow-md"
                  style={{ background: "linear-gradient(135deg, #B39B7A, #8B9B88)" }}>
                  <img src="/dr-tirman-avatar.png" alt="Dr. Hüseyin Tırman" className="absolute inset-0 w-full h-full object-contain object-center" draggable={false} />
                </div>
                <div className="flex-1">
                  <div className="font-display text-sm font-medium mb-1">
                    {lang === "tr" ? "Merhaba, ben Dr. Tırman 👋" : lang === "en" ? "Hi, I'm Dr. Tırman 👋" : lang === "ar" ? "مرحبا، أنا د. تيرمان 👋" : "Здравствуйте, я доктор Тырман 👋"}
                  </div>
                  <div className="text-xs text-muted-foreground leading-relaxed">
                    {lang === "tr"
                      ? "Konuşarak size yardımcı olmamı ister misiniz? Tedaviler, fiyatlar veya randevu hakkında her şeyi sorabilirsiniz."
                      : lang === "en"
                      ? "Would you like me to help you by voice? Ask anything about treatments, pricing, or appointments."
                      : lang === "ar"
                      ? "هل تود أن أساعدك بالحديث؟ اسألني عن العلاجات والأسعار والمواعيد."
                      : "Хотите, помогу вам голосом? Спросите о процедурах, ценах или записи."}
                  </div>
                </div>
              </div>
              <div className="flex gap-2 mt-3">
                <Button
                  data-testid="invite-accept"
                  size="sm"
                  onClick={acceptInvite}
                  className="flex-1 h-8 text-xs rounded-full"
                  style={{ background: "#2C3531" }}
                >
                  <Sparkles className="w-3 h-3 mr-1" />
                  {lang === "tr" ? "Konuşalım" : lang === "en" ? "Let's chat" : lang === "ar" ? "هيا نتحدث" : "Поговорим"}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={dismissInvite}
                  data-testid="invite-later"
                  className="h-8 text-xs rounded-full"
                >
                  {lang === "tr" ? "Sonra" : lang === "en" ? "Later" : lang === "ar" ? "لاحقا" : "Позже"}
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Chat panel */}
      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="chat-panel"
            dir={rtl ? "rtl" : "ltr"}
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ type: "spring", damping: 22, stiffness: 260 }}
            className={isMobile
              ? "fixed inset-0 z-50 flex flex-col bg-white"
              : "fixed bottom-20 right-6 z-50 phone-frame"}
            style={isMobile ? {} : { padding: "12px 10px 18px", borderRadius: 48, background: "#0a0a0a",
                                     boxShadow: "0 25px 60px -10px rgba(0,0,0,0.55), 0 0 0 1px rgba(255,255,255,0.05) inset, 0 0 0 3px #1a1a1a" }}
          >
            <div className={isMobile
              ? "flex flex-col h-full"
              : "relative w-[380px] h-[640px] rounded-[36px] overflow-hidden flex flex-col bg-white"}
              style={isMobile ? {} : { boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.06)" }}
            >
              {/* iPhone Dynamic Island (desktop only) */}
              {!isMobile && (
                <div className="absolute top-2 left-1/2 -translate-x-1/2 w-[92px] h-[28px] rounded-full bg-black z-30 flex items-center justify-end pr-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#1a1a1a] ring-1 ring-white/10" />
                </div>
              )}

              {/* Header — WhatsApp-style on mobile, refined on desktop */}
              <div className={isMobile
                ? "flex items-center justify-between px-3 pt-3 pb-3 text-white"
                : "flex items-center justify-between p-4 pt-10 border-b border-border/40"}
                style={isMobile ? { background: "#000000" } : {}}
              >
                <div className="flex items-center gap-3">
                  <div className={`relative w-10 h-10 sm:w-12 sm:h-12 rounded-full overflow-hidden ring-2 ${isMobile ? "ring-white/30" : "ring-white/70"} shadow-md ${busy ? "avatar-talking" : ""}`}
                    style={{ background: "linear-gradient(135deg, #B39B7A, #8B9B88)" }}>
                    <img src="/dr-tirman-avatar.png" alt="Dr. Hüseyin Tırman" className="absolute inset-0 w-full h-full object-contain object-center" draggable={false} />
                  </div>
                  <div>
                    <div className={`font-display font-medium text-sm flex items-center gap-1.5 ${isMobile ? "text-white" : ""}`}>
                      Dr. Hüseyin Tırman
                      <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-400" title="Çevrimiçi" />
                    </div>
                    <div className={`text-xs ${isMobile ? "text-emerald-100" : "text-muted-foreground"}`}>
                      {busy ? (lang === "tr" ? "yazıyor / konuşuyor..." : lang === "en" ? "typing..." : lang === "ar" ? "يكتب..." : "печатает...") : (isMobile ? (lang === "tr" ? "çevrimiçi" : "online") : t.chat_subtitle)}
                    </div>
                  </div>
                </div>
              <div className="flex items-center gap-2">
                <LanguageSelector value={lang} onChange={setLang} compact />
                <Button
                  data-testid="chat-voice-toggle"
                  size="icon"
                  variant="ghost"
                  onClick={() => setVoiceOn((v) => !v)}
                  className="h-8 w-8"
                  title={voiceOn ? t.chat_voice_on : t.chat_voice_off}
                >
                  {voiceOn ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
                </Button>
                <Button data-testid="chat-close" size="icon" variant="ghost" onClick={() => {
                  setOpen(false);
                  // In embed mode notify the host page so it can close the iframe modal
                  if (embed && typeof window !== "undefined" && window.parent && window.parent !== window) {
                    try { window.parent.postMessage("bilgin-close", "*"); } catch (_) {/* ignore */}
                  }
                }} className="h-8 w-8">
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {/* Messages */}
            <div ref={scrollRef} className={`flex-1 overflow-y-auto chat-scroll p-4 space-y-3 ${isMobile ? "whatsapp-bg" : ""}`}>
              {messages.map((m, i) => (
                <div key={i}>
                  <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`flex ${m.role === "user" ? (rtl ? "justify-start" : "justify-end") : (rtl ? "justify-end" : "justify-start")}`}
                  >
                    <div
                      className={`max-w-[80%] px-4 py-2.5 text-sm whitespace-pre-wrap ${
                        m.role === "user"
                          ? "rounded-3xl rounded-br-md text-white"
                          : "rounded-3xl rounded-bl-md bg-secondary text-foreground"
                      }`}
                      style={m.role === "user" ? { background: "#B39B7A" } : {}}
                    >
                      <MessageContent text={m.content} />
                      {m.needsTap && m.role === "assistant" && (
                        <button
                          data-testid={`replay-msg-${i}`}
                          onClick={() => replayMessage(i)}
                          className="mt-2 inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] bg-white/80 border border-primary/30 hover:bg-white transition-colors"
                        >
                          <Volume2 className="w-3 h-3" />
                          {lang === "tr" ? "🔊 Dinlemek için dokunun" : lang === "en" ? "🔊 Tap to listen" : lang === "ar" ? "🔊 اضغط للاستماع" : "🔊 Нажмите, чтобы прослушать"}
                        </button>
                      )}
                    </div>
                  </motion.div>
                  {/* WhatsApp CTA card — shown for tattoo removal questions */}
                  {m.role === "assistant" && m.whatsappTattoo && (
                    <motion.div
                      data-testid={`whatsapp-tattoo-card-${i}`}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={`mt-2 flex ${rtl ? "justify-end" : "justify-start"}`}
                    >
                      <a
                        href={`https://wa.me/905422313748?text=${encodeURIComponent(
                          lang === "tr" ? "Merhaba, dövme silme fiyatı için dövmemin fotoğrafını paylaşmak istiyorum."
                          : lang === "en" ? "Hello, I'd like to share a photo of my tattoo for a removal quote."
                          : lang === "ar" ? "مرحباً، أود مشاركة صورة وشمي للحصول على عرض سعر لإزالته."
                          : "Здравствуйте, я хочу отправить фото татуировки для оценки удаления."
                        )}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`whatsapp-tattoo-link-${i}`}
                        className="group max-w-[88%] flex items-center gap-3 rounded-2xl px-4 py-3 shadow-md hover:shadow-lg transition-all"
                        style={{
                          background: "linear-gradient(135deg, #25D366 0%, #128C7E 100%)",
                          color: "#fff",
                        }}
                      >
                        <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                          <svg viewBox="0 0 24 24" className="w-6 h-6" fill="currentColor" aria-hidden="true">
                            <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.711.307 1.265.49 1.697.628.713.226 1.362.194 1.875.118.572-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z"/>
                          </svg>
                        </div>
                        <div className="flex-1 text-left">
                          <div className="font-semibold text-sm leading-tight">
                            {lang === "tr" ? "WhatsApp'tan fotoğraf gönderin"
                            : lang === "en" ? "Send a photo via WhatsApp"
                            : lang === "ar" ? "أرسل صورة عبر واتساب"
                            : "Отправьте фото в WhatsApp"}
                          </div>
                          <div className="text-xs opacity-90 mt-0.5">
                            {lang === "tr" ? "+90 542 231 37 48 — Dövme için kesin fiyat"
                            : lang === "en" ? "+90 542 231 37 48 — Exact tattoo quote"
                            : lang === "ar" ? "+90 542 231 37 48 — عرض سعر دقيق"
                            : "+90 542 231 37 48 — Точная цена"}
                          </div>
                        </div>
                        <ExternalLink className="w-4 h-4 opacity-80 flex-shrink-0" />
                      </a>
                    </motion.div>
                  )}
                  {/* WhatsApp CTA card — body Endolift quotes */}
                  {m.role === "assistant" && m.whatsappEndolift && (
                    <motion.div
                      data-testid={`whatsapp-endolift-card-${i}`}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={`mt-2 flex ${rtl ? "justify-end" : "justify-start"}`}
                    >
                      <a
                        href={`https://wa.me/905422313748?text=${encodeURIComponent(
                          lang === "tr" ? "Merhaba, vücutta Endolift uygulaması için ilgilendiğim bölgenin fotoğrafını paylaşmak istiyorum."
                          : lang === "en" ? "Hello, I'd like to share a photo of the body area I'm interested in for an Endolift quote."
                          : lang === "ar" ? "مرحباً، أود مشاركة صورة منطقة جسمي للحصول على عرض سعر Endolift."
                          : "Здравствуйте, я хочу отправить фото зоны тела для оценки Endolift."
                        )}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`whatsapp-endolift-link-${i}`}
                        className="group max-w-[88%] flex items-center gap-3 rounded-2xl px-4 py-3 shadow-md hover:shadow-lg transition-all"
                        style={{ background: "linear-gradient(135deg, #25D366 0%, #128C7E 100%)", color: "#fff" }}
                      >
                        <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                          <svg viewBox="0 0 24 24" className="w-6 h-6" fill="currentColor" aria-hidden="true">
                            <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.711.307 1.265.49 1.697.628.713.226 1.362.194 1.875.118.572-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z"/>
                          </svg>
                        </div>
                        <div className="flex-1 text-left">
                          <div className="font-semibold text-sm leading-tight">
                            {lang === "tr" ? "WhatsApp'tan bölge fotoğrafı gönderin"
                            : lang === "en" ? "Send body area photo via WhatsApp"
                            : lang === "ar" ? "أرسل صورة المنطقة عبر واتساب"
                            : "Отправьте фото зоны в WhatsApp"}
                          </div>
                          <div className="text-xs opacity-90 mt-0.5">
                            {lang === "tr" ? "+90 542 231 37 48 — Vücut Endolift kişiye özel fiyat"
                            : lang === "en" ? "+90 542 231 37 48 — Body Endolift personalised quote"
                            : lang === "ar" ? "+90 542 231 37 48 — Endolift للجسم سعر مخصص"
                            : "+90 542 231 37 48 — Endolift тела индивидуально"}
                          </div>
                        </div>
                        <ExternalLink className="w-4 h-4 opacity-80 flex-shrink-0" />
                      </a>
                    </motion.div>
                  )}
                  {/* Mic permission card — shown right under the first assistant message */}
                  {i === 0 && m.role === "assistant" && micPermission !== "granted" && (
                    <motion.div
                      data-testid="mic-permission-card"
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={`mt-3 flex ${rtl ? "justify-end" : "justify-start"}`}
                    >
                      <div className="max-w-[88%] rounded-2xl border border-primary/30 bg-white/70 backdrop-blur-sm p-3 space-y-2.5 shadow-sm">
                        <div className="flex items-start gap-2">
                          <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                            <Mic className="w-4 h-4" style={{ color: "#B39B7A" }} />
                          </div>
                          <div className="flex-1 text-xs leading-relaxed">
                            <div className="font-display font-medium text-sm mb-1">
                              {lang === "tr" ? "Konuşarak devam etmek ister misiniz?"
                              : lang === "en" ? "Would you like to continue by voice?"
                              : lang === "ar" ? "هل تود المتابعة بالحديث؟"
                              : "Хотите продолжить голосом?"}
                            </div>
                            <div className="text-muted-foreground">
                              {lang === "tr" ? "Tek dokunuşla mikrofon ve hoparlör/kulaklık erişimini birlikte açın — sesli soru sorabilir ve sesli yanıt duyabilirsiniz. (iPhone/Safari kullanıcıları için ses çıkışı kilidini açar.)"
                              : lang === "en" ? "One tap enables both microphone and speaker/headphones — ask by voice and hear spoken replies. (Also unlocks audio output on iPhone/Safari.)"
                              : lang === "ar" ? "نقرة واحدة لتفعيل الميكروفون والسماعة معًا — اسأل صوتيًا واسمع الإجابات. (يفتح أيضًا قفل الصوت على iPhone/Safari.)"
                              : "Одно нажатие включает микрофон и динамик/наушники — задавайте вопросы голосом и слушайте ответы. (Также разблокирует звук на iPhone/Safari.)"}
                            </div>
                          </div>
                        </div>
                        {micPermission === "denied" ? (
                          <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2 leading-relaxed">
                            {lang === "tr" ? "⚠️ Mikrofon izni reddedilmiş. Tarayıcının adres çubuğundaki kilit simgesinden 'Mikrofon → İzin ver' diyerek açabilirsiniz."
                            : lang === "en" ? "⚠️ Microphone is blocked. Click the lock icon in the address bar → Microphone → Allow."
                            : lang === "ar" ? "⚠️ الميكروفون محظور. انقر على رمز القفل في شريط العناوين ← الميكروفون ← السماح."
                            : "⚠️ Микрофон заблокирован. Нажмите на иконку замка в адресной строке → Микрофон → Разрешить."}
                          </div>
                        ) : (
                          <div className="flex gap-2">
                            <Button
                              data-testid="mic-allow-btn"
                              size="sm"
                              onClick={requestMicPermission}
                              disabled={micRequesting}
                              className="flex-1 h-9 text-xs rounded-full"
                              style={{ background: "#B39B7A" }}
                            >
                              <Mic className="w-3 h-3 mr-1.5" />
                              {micRequesting
                                ? (lang === "tr" ? "İstek gönderiliyor..." : lang === "en" ? "Requesting..." : lang === "ar" ? "يطلب الإذن..." : "Запрос...")
                                : (lang === "tr" ? "Mikrofon ve sesi aç" : lang === "en" ? "Allow mic & audio" : lang === "ar" ? "اسمح بالميكروفون والصوت" : "Включить микрофон и звук")}
                            </Button>
                            <Button
                              data-testid="mic-skip-btn"
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setMicPermission("granted"); // hide card; user can still type
                                try { localStorage.setItem("drht_mic_perm", "granted"); } catch {/* ignore */}
                              }}
                              className="h-9 text-xs rounded-full"
                            >
                              {lang === "tr" ? "Yazarak devam" : lang === "en" ? "Type instead" : lang === "ar" ? "أكتب بدلاً" : "Текстом"}
                            </Button>
                          </div>
                        )}
                      </div>
                    </motion.div>
                  )}
                </div>
              ))}
              {busy && (
                <div className={`flex ${rtl ? "justify-end" : "justify-start"}`}>
                  <div className="bg-secondary rounded-3xl rounded-bl-md px-4 py-2.5">
                    <VoiceWaveform active />
                  </div>
                </div>
              )}

              {/* Inline soft "Would you like contact?" prompt — Yes/No only */}
              <AnimatePresence>
                {askContactInline && !showLead && !leadDone && (
                  <motion.div
                    data-testid="ask-contact-inline"
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -4 }}
                    className={`flex gap-2 ${rtl ? "justify-end" : "justify-start"}`}
                  >
                    <Button
                      data-testid="ask-contact-yes"
                      size="sm"
                      onClick={() => {
                        setAskContactInline(false);
                        setShowLead(true);
                      }}
                      className="h-9 text-xs rounded-full px-4"
                      style={{ background: "#B39B7A" }}
                    >
                      {lang === "tr" ? "✓ Evet, lütfen" : lang === "en" ? "✓ Yes, please" : lang === "ar" ? "✓ نعم، من فضلك" : "✓ Да, пожалуйста"}
                    </Button>
                    <Button
                      data-testid="ask-contact-no"
                      size="sm"
                      variant="outline"
                      onClick={() => setAskContactInline(false)}
                      className="h-9 text-xs rounded-full px-4"
                    >
                      {lang === "tr" ? "Hayır, teşekkürler" : lang === "en" ? "No, thanks" : lang === "ar" ? "لا، شكراً" : "Нет, спасибо"}
                    </Button>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Lead form inline */}
              <AnimatePresence>
                {showLead && !leadDone && (
                  <motion.form
                    data-testid="lead-form"
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    onSubmit={handleLead}
                    className="bg-white/80 border border-border/50 rounded-2xl p-4 space-y-3"
                  >
                    <div className="font-display font-medium text-sm">
                      {isCampaignLead
                        ? (lang === "tr" ? "🎁 Kampanya Bilgisi"
                          : lang === "en" ? "🎁 Campaign Details"
                          : lang === "ar" ? "🎁 تفاصيل العرض"
                          : "🎁 Информация об акции")
                        : t.lead_title}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {isCampaignLead
                        ? (lang === "tr" ? "Aktif kampanyalarımızı görmek için iletişim bilgilerinizi paylaşın."
                          : lang === "en" ? "Share your contact details to see our active campaigns."
                          : lang === "ar" ? "شارك بيانات الاتصال لرؤية العروض النشطة."
                          : "Оставьте контакты, чтобы увидеть активные акции.")
                        : t.lead_sub}
                    </div>
                    <Input
                      data-testid="lead-name"
                      placeholder={t.lead_name}
                      value={leadData.name}
                      onChange={(e) => setLeadData({ ...leadData, name: e.target.value })}
                      required
                    />
                    <Select
                      value={leadData.contact_method}
                      onValueChange={(v) => setLeadData({ ...leadData, contact_method: v })}
                    >
                      <SelectTrigger data-testid="lead-method"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="phone">{t.lead_phone}</SelectItem>
                        <SelectItem value="email">{t.lead_email}</SelectItem>
                        <SelectItem value="whatsapp">{t.lead_whatsapp}</SelectItem>
                      </SelectContent>
                    </Select>
                    <Input
                      data-testid="lead-value"
                      placeholder={t.lead_value}
                      value={leadData.contact_value}
                      onChange={(e) => setLeadData({ ...leadData, contact_value: e.target.value })}
                      required
                    />
                    <Button data-testid="lead-submit" type="submit" className="w-full" style={{ background: "#B39B7A" }}>
                      {t.lead_submit}
                    </Button>
                    {!showDeclineForm ? (
                      <button
                        type="button"
                        data-testid="lead-decline"
                        onClick={() => setShowDeclineForm(true)}
                        className="w-full text-xs text-muted-foreground hover:text-foreground underline-offset-2 hover:underline"
                      >
                        {lang === "tr" ? "Şu an istemiyorum" : lang === "en" ? "Not right now" : lang === "ar" ? "ليس الآن" : "Не сейчас"}
                      </button>
                    ) : (
                      <div className="space-y-2 pt-2 border-t border-border/40">
                        <div className="text-xs text-muted-foreground">
                          {lang === "tr" ? "Sebebini paylaşır mısınız? (opsiyonel)" : lang === "en" ? "Could you share why? (optional)" : lang === "ar" ? "هل يمكنك مشاركة السبب؟ (اختياري)" : "Поделитесь причиной? (опционально)"}
                        </div>
                        <Input
                          data-testid="decline-reason"
                          value={declineText}
                          onChange={(e) => setDeclineText(e.target.value)}
                          placeholder={lang === "tr" ? "ör: fiyat, zamanlama..." : "e.g. price, timing..."}
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          data-testid="decline-submit"
                          onClick={handleDecline}
                          className="w-full"
                        >
                          {lang === "tr" ? "Gönder" : "Submit"}
                        </Button>
                      </div>
                    )}
                  </motion.form>
                )}
              </AnimatePresence>
            </div>

            {/* Input area */}
            <div className="p-3 border-t border-border/40 bg-white/40">
              {/* FAQ expandable chips */}
              <AnimatePresence>
                {showFaq && faqList.length > 0 && (
                  <motion.div
                    data-testid="faq-list"
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    className="mb-2 overflow-hidden"
                  >
                    <div className="flex flex-wrap gap-1.5 pb-2">
                      {faqList.map((q, i) => (
                        <button
                          key={i}
                          data-testid={`faq-chip-${i}`}
                          onClick={() => { setShowFaq(false); send(q); }}
                          disabled={busy}
                          className="text-[11px] px-2.5 py-1.5 rounded-full bg-white border border-border/60 hover:border-primary/60 hover:bg-secondary/40 transition-colors text-left max-w-full truncate"
                          title={q}
                        >
                          {q}
                        </button>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              <div className="flex items-center gap-2">
                <button
                  data-testid="chat-mic"
                  type="button"
                  onPointerDown={handleMicPointerDown}
                  onPointerUp={handleMicPointerUp}
                  onPointerLeave={handleMicPointerCancel}
                  onPointerCancel={handleMicPointerCancel}
                  onContextMenu={(e) => e.preventDefault()}
                  disabled={busy}
                  className={`relative h-12 w-12 rounded-full flex items-center justify-center text-white transition-all select-none ${recording ? "scale-110" : "hover:scale-105"} ${busy ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
                  style={{ background: recording ? "#ef4444" : "#8B9B88", WebkitTapHighlightColor: "transparent", touchAction: "none" }}
                  title={lang === "tr" ? "Konuşmak için BASILI TUT, bitince bırak" : lang === "en" ? "HOLD to talk, release to send" : lang === "ar" ? "اضغط مع الاستمرار للتحدث" : "Удерживайте, чтобы говорить"}
                >
                  {recording && <span className="absolute inset-0 rounded-full animate-ping bg-red-500/60" />}
                  {recording ? <MicOff className="w-5 h-5 relative" /> : <Mic className="w-5 h-5 relative" />}
                </button>
                <Input
                  data-testid="chat-input"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && send()}
                  placeholder={recording ? t.chat_listening : t.chat_placeholder}
                  disabled={busy || recording}
                  className="bg-white border-border/40"
                />
                <Button
                  data-testid="chat-send"
                  size="icon"
                  onClick={() => send()}
                  disabled={busy || !input.trim()}
                  className="h-10 w-10 rounded-full"
                  style={{ background: "#B39B7A" }}
                >
                  <Send className="w-4 h-4" />
                </Button>
              </div>

              <div className="mt-2 flex items-center justify-between gap-2 text-[11px]">
                <button
                  data-testid="faq-toggle"
                  onClick={loadFaq}
                  disabled={faqLoading}
                  className="text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1 underline-offset-2 hover:underline"
                >
                  <Sparkles className="w-3 h-3" />
                  {faqLoading
                    ? (lang === "tr" ? "yükleniyor..." : lang === "en" ? "loading..." : lang === "ar" ? "جار التحميل..." : "загрузка...")
                    : showFaq
                      ? (lang === "tr" ? "Gizle" : lang === "en" ? "Hide" : lang === "ar" ? "إخفاء" : "Скрыть")
                      : (lang === "tr" ? "Sık sorulan sorular" : lang === "en" ? "Frequently asked" : lang === "ar" ? "الأسئلة الشائعة" : "Частые вопросы")}
                </button>
              </div>
              {recording && (
                <div className="mt-2 flex items-center gap-2 text-xs text-red-600 font-medium">
                  <VoiceWaveform active color="#ef4444" />
                  <span>{lang === "tr" ? "Kayıtta... bitince mikrofonu BIRAKIN" : lang === "en" ? "Recording... RELEASE the mic when done" : lang === "ar" ? "جار التسجيل... حرر الميكروفون عند الانتهاء" : "Запись... ОТПУСТИТЕ микрофон по окончании"}</span>
                </div>
              )}
              {!recording && micPermission === "granted" && (
                <div className="mt-1.5 text-[10px] text-muted-foreground text-center select-none">
                  {lang === "tr" ? "Konuşmak için mikrofonu basılı tutun" : lang === "en" ? "Hold the mic button to speak" : lang === "ar" ? "اضغط مع الاستمرار على الميكروفون للتحدث" : "Удерживайте кнопку микрофона, чтобы говорить"}
                </div>
              )}
            </div>
            </div>
            {/* "made with emergent" — outside the phone frame on desktop, small line on mobile */}
            <div
              className={isMobile
                ? "px-3 py-1.5 text-center text-[9px] tracking-[0.18em] uppercase text-muted-foreground/70 font-light select-none bg-white"
                : "absolute -bottom-6 left-1/2 -translate-x-1/2 text-center text-[9px] tracking-[0.18em] uppercase font-light select-none whitespace-nowrap"}
              style={isMobile ? {} : { color: "rgba(255,255,255,0.45)" }}
            >
              made with emergent
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
